/**
 * Cart Drawer JavaScript
 * Handles cart drawer open/close and cart operations
 * Uses Shopify Section Rendering API for AJAX updates (no page reload)
 *
 * Converted from: site/modules/cart/cart-drawer.tsx (481 lines Preact)
 */

class CartDrawer extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Ensure children are parsed before querying
    // Using setTimeout to defer slightly if needed, but connectedCallback is usually safe for parsed HTML
    if (this.initialized) return;

    this.drawer = this.querySelector('.cart-drawer__panel');
    this.overlay = this.querySelector('.cart-drawer__overlay');
    this.itemsContainer = this.querySelector('#cart-drawer-items');
    this.footer = this.querySelector('.cart-drawer__footer');
    this.shippingBar = this.querySelector('.cart-drawer__shipping');

    // Debounce timer for quantity input
    this.updateTimeout = null;
    this.isUpdating = false;

    this.init();
    this.initialized = true;
  }

  init() {
    // Cart trigger buttons
    document.querySelectorAll('[data-cart-trigger]').forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close buttons
    document.querySelectorAll('[data-cart-close]').forEach(closer => {
      closer.addEventListener('click', () => this.close());
    });

    // Quantity buttons (delegated)
    this.addEventListener('click', (e) => {
      const target = e.target.closest('[data-qty-minus], [data-qty-plus], [data-remove]');
      if (!target) return;

      if (target.matches('[data-qty-minus]')) {
        this.updateQuantity(target, -1);
      } else if (target.matches('[data-qty-plus]')) {
        this.updateQuantity(target, 1);
      } else if (target.matches('[data-remove]')) {
        this.removeItem(target);
      }
    });

    // Manual quantity input change (debounced)
    this.addEventListener('change', (e) => {
      if (e.target.matches('.cart-item__qty-input')) {
        this.onQuantityInputChange(e.target);
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });

    // Close on overlay click
    this.overlay?.addEventListener('click', () => this.close());

    // Listen for cart:add events from product forms
    document.addEventListener('cart:add', () => {
      this.refreshCart().then(() => this.open());
    });
  }

  isOpen() {
    return this.dataset.open === 'true';
  }

  open() {
    this.dataset.open = 'true';
    document.body.style.overflow = 'hidden';

    // Focus first focusable element for accessibility
    const closeBtn = this.querySelector('[data-cart-close]');
    closeBtn?.focus();

    // Trap focus
    this.trapFocus();
  }

  close() {
    this.dataset.open = 'false';
    document.body.style.overflow = '';

    // Return focus to trigger
    const trigger = document.querySelector('[data-cart-trigger]');
    trigger?.focus();
  }

  trapFocus() {
    const focusableElements = this.drawer?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusableElements?.length) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTab = (e) => {
      if (e.key !== 'Tab' || !this.isOpen()) return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    this.drawer?.addEventListener('keydown', handleTab);
  }

  setLoading(lineItem, isLoading) {
    if (isLoading) {
      lineItem?.classList.add('cart-item--loading');
      lineItem?.setAttribute('aria-busy', 'true');
    } else {
      lineItem?.classList.remove('cart-item--loading');
      lineItem?.removeAttribute('aria-busy');
    }
  }

  async updateQuantity(button, delta) {
    if (this.isUpdating) return;

    const line = parseInt(button.dataset.line, 10);
    const lineItem = this.querySelector(`[data-line-item][data-line="${line}"]`);
    const input = this.querySelector(`input[data-line="${line}"]`);
    if (!input) return;

    const currentQty = parseInt(input.value, 10);
    const newQty = Math.max(0, currentQty + delta);

    if (newQty === 0) {
      return this.removeItem(button);
    }

    // Optimistic UI update
    input.value = newQty;
    this.setLoading(lineItem, true);

    try {
      await this.changeCart(line, newQty);
    } catch (error) {
      // Revert on error
      input.value = currentQty;
      this.showError('Could not update quantity. Please try again.');
      console.error('Cart update error:', error);
    } finally {
      this.setLoading(lineItem, false);
    }
  }

  onQuantityInputChange(input) {
    clearTimeout(this.updateTimeout);

    const line = parseInt(input.dataset.line, 10);
    const newQty = Math.max(0, parseInt(input.value, 10) || 0);

    // Debounce the update
    this.updateTimeout = setTimeout(async () => {
      const lineItem = this.querySelector(`[data-line-item][data-line="${line}"]`);
      this.setLoading(lineItem, true);

      try {
        await this.changeCart(line, newQty);
      } catch (error) {
        this.showError('Could not update quantity. Please try again.');
        console.error('Cart update error:', error);
      } finally {
        this.setLoading(lineItem, false);
      }
    }, 500);
  }

  async removeItem(button) {
    if (this.isUpdating) return;

    const line = parseInt(button.dataset.line, 10);
    const lineItem = this.querySelector(`[data-line-item][data-line="${line}"]`);

    // Optimistic UI - fade out item
    lineItem?.classList.add('cart-item--removing');
    this.setLoading(lineItem, true);

    try {
      await this.changeCart(line, 0);
    } catch (error) {
      lineItem?.classList.remove('cart-item--removing');
      this.showError('Could not remove item. Please try again.');
      console.error('Cart remove error:', error);
    } finally {
      this.setLoading(lineItem, false);
    }
  }

  async changeCart(line, quantity) {
    this.isUpdating = true;

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });

      if (!response.ok) {
        throw new Error(`Cart update failed: ${response.status}`);
      }

      const cart = await response.json();

      // Update header cart count immediately
      this.updateCartCount(cart.item_count);
      this.updateCartSubtotal(cart.total_price);

      // Refresh drawer content via Section Rendering API
      await this.refreshCart();

      return cart;
    } finally {
      this.isUpdating = false;
    }
  }

  async refreshCart() {
    try {
      // Fetch updated cart drawer HTML via Section Rendering API
      const response = await fetch(`${window.location.pathname}?sections=cart-drawer`);

      if (!response.ok) {
        throw new Error('Failed to fetch cart section');
      }

      const sections = await response.json();
      const html = sections['cart-drawer'];

      if (html) {
        // Parse new HTML and update drawer content
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Update items container
        const newItems = doc.querySelector('#cart-drawer-items');
        if (newItems && this.itemsContainer) {
          this.itemsContainer.innerHTML = newItems.innerHTML;
        }

        // Update footer (subtotal, etc.)
        const newFooter = doc.querySelector('.cart-drawer__footer');
        const currentFooter = this.querySelector('.cart-drawer__footer');
        if (newFooter && currentFooter) {
          currentFooter.innerHTML = newFooter.innerHTML;
        } else if (newFooter && !currentFooter) {
          // Footer didn't exist before (cart was empty), add it
          this.drawer?.appendChild(newFooter.cloneNode(true));
        } else if (!newFooter && currentFooter) {
          // Cart is now empty, remove footer
          currentFooter.remove();
        }

        // Update shipping progress bar
        const newShipping = doc.querySelector('.cart-drawer__shipping');
        const currentShipping = this.querySelector('.cart-drawer__shipping');
        if (newShipping && currentShipping) {
          currentShipping.innerHTML = newShipping.innerHTML;
        }
      }
    } catch (error) {
      console.error('Failed to refresh cart drawer:', error);
      // Fallback: reload page if section rendering fails
      window.location.reload();
    }
  }

  updateCartCount(count) {
    const countElements = document.querySelectorAll('[data-cart-count]');
    countElements.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';

      // Add bounce animation
      el.classList.add('cart-count--updated');
      setTimeout(() => el.classList.remove('cart-count--updated'), 300);
    });
  }

  updateCartSubtotal(totalPrice) {
    const subtotalElements = document.querySelectorAll('[data-cart-subtotal]');
    subtotalElements.forEach(el => {
      el.textContent = this.formatMoney(totalPrice);
    });
  }

  formatMoney(cents) {
    // Use Shopify's money format if available, otherwise fallback
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD'
    }).format(dollars);
  }

  showError(message) {
    // Check for existing error element or create one
    let errorEl = this.querySelector('.cart-drawer__error');
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'cart-drawer__error';
      errorEl.setAttribute('role', 'alert');
      this.drawer?.insertBefore(errorEl, this.itemsContainer);
    }

    errorEl.textContent = message;
    errorEl.hidden = false;

    // Auto-hide after 5 seconds
    setTimeout(() => {
      errorEl.hidden = true;
    }, 5000);
  }
}

customElements.define('cart-drawer', CartDrawer);

// Global cart utilities
window.StuffedStore = window.StuffedStore || {};

window.StuffedStore.getCart = async function () {
  const response = await fetch('/cart.js');
  return response.json();
};

window.StuffedStore.openCartDrawer = function () {
  const drawer = document.querySelector('cart-drawer');
  drawer?.open();
};

window.StuffedStore.closeCartDrawer = function () {
  const drawer = document.querySelector('cart-drawer');
  drawer?.close();
};
